import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import type { BookingWizardState } from "@/lib/actions/booking";
import { addDays } from "@/components/trip/DeparturesPanel";
import { getRecaptchaToken } from "@/lib/recaptcha";
import {
  normalizeAddons,
  normalizeTiers,
  resolvePerPersonPrice,
  type NormalizedTier,
} from "@/lib/trip/normalize";
import type { validateLeadTraveler as ValidateLeadTraveler } from "@/lib/booking/schema";
import type { GroupPrice, PackageTier, PackageAddon } from "@/lib/dto";

// The lead-traveller schema is zod-based. Load it on demand (the wizard warms
// it on mount) so zod stays out of the shared client chunks that every page —
// including the homepage — downloads.
let leadTravelerValidator: typeof ValidateLeadTraveler | null = null;
const loadLeadTravelerValidator = async () =>
  (leadTravelerValidator ??= (await import("@/lib/booking/schema"))
    .validateLeadTraveler);

export const STEP_LABELS = ["Package", "Traveller", "Payment"] as const;
export const TOTAL_STEPS = STEP_LABELS.length;

// Card processing fee applied to the amount paid online (deposit or full),
// mirrored from the reference summary design. The backend invoice is the
// authoritative amount — this is a pre-booking estimate only.
export const BANK_CHARGE_RATE = 0.035;

// Client-side mirror of the server's BOOKING_CREATE_ENABLED flag, used only to
// pick the CTA label ("Continue to Payment" vs "Send Booking Request"). The
// server action is the authority on which path actually runs; this is cosmetic.
export const BOOKING_CREATE_UI =
  process.env.NEXT_PUBLIC_BOOKING_CREATE_ENABLED === "1";

export const FIND_US_OPTIONS = [
  "Google",
  "Facebook",
  "Yahoo",
  "Bing",
  "Trip Advisor",
  "Friend",
  "Client Reference",
  "I am a regular client",
  "Others",
];

export const MONTH_OPTIONS = [
  { value: "01", label: "Jan" },
  { value: "02", label: "Feb" },
  { value: "03", label: "Mar" },
  { value: "04", label: "Apr" },
  { value: "05", label: "May" },
  { value: "06", label: "Jun" },
  { value: "07", label: "Jul" },
  { value: "08", label: "Aug" },
  { value: "09", label: "Sep" },
  { value: "10", label: "Oct" },
  { value: "11", label: "Nov" },
  { value: "12", label: "Dec" },
];

/** `YYYY-MM-DD` in local time — the `/api/booking` backend rejects ISO datetimes. */
function isoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function dayOptions() {
  const days: string[] = [];
  for (let d = 1; d <= 31; d++) days.push(d < 10 ? `0${d}` : String(d));
  return days;
}

export type BookingWizardFormArgs = {
  packageTitle: string;
  packageSlug: string;
  packageId?: number;
  departureId?: number;
  currency: string;
  tier?: NormalizedTier | null;
  startDate?: Date | null;
  travelers?: number;
  pricing?: {
    group_prices: GroupPrice[];
    tiers: Record<string, PackageTier> | null;
  };
  addons?: PackageAddon[];
  duration?: number;
  durationUnit?: string;
  open: boolean;
};

/**
 * All non-JSX state, derived pricing, validation, and submit logic for
 * BookingWizard — extracted so the component itself stays focused on markup.
 * Nothing here should read/write the DOM beyond the redirect side effect.
 */
export function useBookingWizardForm({
  packageTitle,
  packageSlug,
  packageId,
  departureId,
  currency,
  tier,
  startDate: initialDate,
  travelers: initialTravelers = 1,
  pricing,
  addons: rawAddons = [],
  duration,
  durationUnit,
  open,
}: BookingWizardFormArgs) {
  const [currentStep, setCurrentStep] = useState(1);
  const [ts] = useState(() => String(Date.now()));
  const [pageUrl] = useState(() =>
    typeof window !== "undefined" ? window.location.href : "",
  );
  const submittingRef = useRef(false);
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  // Skip the focus-move on mount (nothing to announce yet — the dialog
  // trigger already has focus at that point).
  const isFirstRender = useRef(true);

  // Move focus to the new step's heading whenever the step changes, so
  // keyboard/screen-reader users are moved to (and hear) the new context
  // instead of focus staying on the now-hidden "Continue"/"Back" button.
  // Gated on `open` so the step-1 reset on dialog close doesn't steal focus
  // back from Radix, which already returns it to the trigger on close.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!open) return;
    stepHeadingRef.current?.focus();
  }, [currentStep, open]);

  // Resolve add-ons (title/price/description) once — the raw API shape mixes
  // field names and the wizard historically read the wrong ones, zeroing every
  // add-on price. `normalizeAddons` is the single source of truth.
  const addons = useMemo(() => normalizeAddons(rawAddons), [rawAddons]);

  // Step 1: Package options
  const [selectedDate] = useState<Date | null>(initialDate ?? null);
  const [travelers, setTravelers] = useState(initialTravelers);
  const [selectedAddons, setSelectedAddons] = useState<Record<string, number>>(
    () => Object.fromEntries(addons.map((a) => [a.id, 0])),
  );

  // Step 2: Participant info
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [nationality, setNationality] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [passportNo, setPassportNo] = useState("");
  const [gender, setGender] = useState("");
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [findingUs, setFindingUs] = useState("");
  const [customFindingUs, setCustomFindingUs] = useState("");
  const [message, setMessage] = useState("");
  const [step2Errors, setStep2Errors] = useState<Record<string, string>>({});

  // Step 3: Review — payment preference (informational only, no charge is made)
  const [paymentPreference, setPaymentPreference] = useState<
    "partial_payment" | "fully_payment"
  >("partial_payment");
  const [terms, setTerms] = useState(false);

  // This dialog's content never renders in the initial server HTML (Radix
  // only mounts it once opened client-side), so reading the real year here
  // carries no hydration-mismatch risk.
  const [currentYear] = useState(() => new Date().getFullYear());
  const yearOptions = useMemo(() => {
    const list: string[] = [];
    for (let y = currentYear - 85; y <= currentYear; y++) list.push(String(y));
    return list;
  }, [currentYear]);
  const dayList = useMemo(() => dayOptions(), []);

  // Submit state. Booking goes through the `/api/booking` route handler, which
  // proxies to the travel API and returns `{ bookingId, invoiceToken,
  // paymentRedirectUrl }`.
  const router = useRouter();
  const [state, setState] = useState<BookingWizardState | null>(null);
  const [pending, setPending] = useState(false);

  // Route the customer onward on success: HBL's hosted checkout when the
  // backend already produced one, otherwise the invoice page (which can start
  // payment itself), otherwise the booking confirmation page.
  const redirectTarget =
    state?.ok && state.paymentRedirectUrl
      ? state.paymentRedirectUrl
      : state?.ok && state.invoiceToken
        ? `/invoice/${encodeURIComponent(state.invoiceToken)}`
        : state?.ok && state.reference != null
          ? `/booking/${encodeURIComponent(String(state.reference))}`
          : null;
  const redirecting = redirectTarget != null;
  useEffect(() => {
    if (!redirectTarget) return;
    // An absolute gateway URL leaves the app entirely; internal paths use the
    // router so the client transition is preserved.
    if (/^https?:\/\//i.test(redirectTarget)) {
      window.location.href = redirectTarget;
    } else {
      router.push(redirectTarget);
    }
  }, [redirectTarget, router]);

  const resetToStepOne = () => setCurrentStep(1);

  // Schema field names -> the wizard's own local error keys (phone/dob use
  // different names locally than in leadTravelerSchema).
  const mapSchemaErrors = (errors: Record<string, string>) => {
    const mapped: Record<string, string> = { ...errors };
    if (errors.phone) mapped.mobileNumber = errors.phone;
    if (errors.dateOfBirth) mapped.dob = errors.dateOfBirth;
    return mapped;
  };

  const step2Values = () => ({
    name,
    email,
    countryCode,
    phone: mobileNumber,
    nationality,
    passportNo,
    gender,
    dateOfBirth: day && month && year ? `${year}-${month}-${day}` : "",
  });

  useEffect(() => {
    void loadLeadTravelerValidator();
  }, []);

  const validateStep2 = async () => {
    const validate = await loadLeadTravelerValidator();
    const errors = validate(step2Values());
    setStep2Errors(mapSchemaErrors(errors));
    return Object.keys(errors).length === 0;
  };

  // Validate a single field on blur so errors surface as the user fills the
  // form instead of all at once on "Continue". `localKey` is the field's key
  // in `step2Errors` (may differ from the schema key — see mapSchemaErrors).
  const validateFieldOnBlur = (localKey: string) => {
    // Not loaded yet (first moments after mount) — the step-2 check still runs.
    if (!leadTravelerValidator) return;
    const errors = mapSchemaErrors(leadTravelerValidator(step2Values()));
    setStep2Errors((prev) => {
      const next = { ...prev };
      if (errors[localKey]) next[localKey] = errors[localKey];
      else delete next[localKey];
      return next;
    });
  };

  const nextStep = async () => {
    if (currentStep === 2 && !(await validateStep2())) return;
    setCurrentStep(Math.min(currentStep + 1, TOTAL_STEPS));
  };

  const prevStep = () => {
    setCurrentStep(Math.max(currentStep - 1, 1));
  };

  // Compute totals for the review step. These are an *estimate* — the
  // authoritative amount is the invoice the backend issues after booking.
  //
  // A tier-priced package carries its brackets on the tier, not reliably on
  // `pricing.group_prices` (which, on this backend, is a cross-tier UNION —
  // showing every tier's brackets at once). So the group-price table must be
  // scoped to exactly one tier:
  //   1. the caller-selected tier, when one was passed; else
  //   2. the first tier (by display order) — the effective default.
  // Only a package with no tiers at all falls back to `pricing.group_prices`.
  const defaultTier = useMemo(
    () => (tier ? null : (normalizeTiers(pricing?.tiers)[0] ?? null)),
    [tier, pricing?.tiers],
  );
  const effectiveTier = tier ?? defaultTier;
  const groupPrices = effectiveTier?.groupPrices?.length
    ? effectiveTier.groupPrices
    : effectiveTier
      ? []
      : (pricing?.group_prices ?? []);
  const pricePerPerson = resolvePerPersonPrice(
    { defaultPrice: effectiveTier?.defaultPrice, groupPrices },
    travelers,
  );
  const tierLabel = tier?.alias || tier?.name || "";
  // Label for the group-rate table — names the tier those brackets belong to,
  // including the implicit first-tier default the user hasn't actively picked.
  const effectiveTierLabel = effectiveTier?.alias || effectiveTier?.name || "";
  const tripTotal = pricePerPerson * travelers;
  const addonsTotal = addons.reduce(
    (sum, addon) => sum + addon.price * (selectedAddons[addon.id] ?? 0),
    0,
  );
  const grandTotal = tripTotal + addonsTotal;

  // Deposit estimate for the review summary. The real amount due (and any
  // card/bank fee) comes from the backend invoice — shown here only as a
  // pre-booking estimate, clearly labelled as such in the UI.
  //
  // Kept unrounded until the final display/submit boundary: rounding the
  // deposit to a whole unit *before* taking 3.5% of it (the previous bug)
  // compounds — e.g. a 133 grand total rounds its 26.6 deposit up to 27
  // first, then charges 3.5% of 27 instead of of the true 26.6, so neither
  // the deposit nor the fee is actually the advertised percentage anymore.
  // `formatMoney` already rounds to 2dp for display; `handleSubmit` rounds
  // once, at the end, for the wire payload.
  const depositAmount =
    paymentPreference === "partial_payment" ? grandTotal * 0.2 : grandTotal;
  const bankCharge = depositAmount * BANK_CHARGE_RATE;
  const depositEstimate = depositAmount + bankCharge;

  const isDays = (durationUnit || "days").toLowerCase().startsWith("day");
  const endDate =
    selectedDate && isDays && duration && duration > 1
      ? addDays(selectedDate, duration - 1)
      : selectedDate;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setPending(true);
    setState(null);

    try {
      // Real reCAPTCHA v3 integration — already wired project-wide, fails
      // open ('') when the tenant has no site key configured.
      const captchaToken = await getRecaptchaToken("booking");

      const res = await fetch("/api/booking/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          packageId,
          packageTitle,
          packageSlug,
          tier: tier?.key || undefined,
          departureId,
          startDate: selectedDate ? isoDate(selectedDate) : undefined,
          endDate: endDate ? isoDate(endDate) : undefined,
          travelers,
          pricePerPerson,
          // The backend has no separate bank-charge field — it derives the
          // deposit/full amount by applying `paymentOption`'s percentage
          // directly to `totalPrice` (confirmed on nepalhightrek.com: a
          // partial-payment booking with `totalPrice: grandTotal` redirected
          // to the bank for exactly 20% of the plain trip price, with the fee
          // missing entirely). So the fee has to be baked into `totalPrice`
          // itself, *before* the backend's percentage split, not added after:
          // 20% of (grandTotal × 1.035) equals (20% of grandTotal) × 1.035,
          // so this produces the same fee-inclusive deposit/full amount
          // shown here.
          totalPrice: Math.round(grandTotal * (1 + BANK_CHARGE_RATE) * 100) / 100,
          currency,
          leadTravelerName: name,
          leadTravelerEmail: email,
          leadTravelerPhone: [countryCode, mobileNumber]
            .filter(Boolean)
            .join(" "),
          country: nationality,
          leadTravelerDateOfBirth:
            day && month && year ? `${year}-${month}-${day}` : undefined,
          leadTravelerPassportNumber: passportNo || undefined,
          specialRequests: message || undefined,
          findingUs:
            (findingUs === "Others" ? customFindingUs : findingUs) || undefined,
          legalAccepted: terms,
          paymentOption: paymentPreference,
          // Anti-spam / attribution context.
          ts,
          url: pageUrl,
          // `{ id, count, title, price }` — the backend validator requires
          // `count` (not `qty`) as a number.
          addOns: addons
            .filter((a) => (selectedAddons[a.id] ?? 0) > 0)
            .map((a) => ({
              id: a.id,
              title: a.title,
              price: a.price,
              count: selectedAddons[a.id] ?? 0,
            })),
          captcha_response: captchaToken || undefined,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setState({
          ok: false,
          code: res.status === 429 ? "rate_limited" : "server",
          message:
            data?.error ?? "Could not complete your booking. Please try again.",
        });
        return;
      }

      setState({
        ok: true,
        reference: data.bookingId,
        invoiceToken: data.invoiceToken ?? null,
        paymentRedirectUrl: data.paymentRedirectUrl ?? null,
        message:
          data.message ?? "Booking confirmed — continue to secure payment.",
      });
    } catch {
      setState({
        ok: false,
        code: "server",
        message:
          "Could not reach the booking service. Please check your connection and try again.",
      });
    } finally {
      submittingRef.current = false;
      // Keep the button disabled while a redirect is in flight; `redirecting`
      // covers that case, so it is safe to clear pending here.
      setPending(false);
    }
  };

  return {
    currentStep,
    stepHeadingRef,
    addons,
    selectedDate,
    travelers,
    setTravelers,
    selectedAddons,
    setSelectedAddons,
    name,
    setName,
    email,
    setEmail,
    nationality,
    setNationality,
    countryCode,
    setCountryCode,
    mobileNumber,
    setMobileNumber,
    passportNo,
    setPassportNo,
    gender,
    setGender,
    day,
    setDay,
    month,
    setMonth,
    year,
    setYear,
    findingUs,
    setFindingUs,
    customFindingUs,
    setCustomFindingUs,
    message,
    setMessage,
    step2Errors,
    paymentPreference,
    setPaymentPreference,
    terms,
    setTerms,
    yearOptions,
    dayList,
    state,
    pending,
    redirecting,
    resetToStepOne,
    validateFieldOnBlur,
    nextStep,
    prevStep,
    groupPrices,
    pricePerPerson,
    tierLabel,
    effectiveTierLabel,
    tripTotal,
    grandTotal,
    depositAmount,
    bankCharge,
    depositEstimate,
    endDate,
    handleSubmit,
  };
}
